/**
 * FiberCountPicker - voľba počtu vlákien kábla.
 *
 * Pridané 2026-09-30. Používa sa v paneli „Kábel klikaním" aj v dialógu
 * úpravy kábla, aby sa počet vlákien dal zadať rovnako na oboch miestach.
 *
 * Vedľa výberu z bežných hodnôt je aj políčko na vlastné číslo — káble sa
 * vyrábajú aj v počtoch, ktoré v zozname nie sú.
 *
 * Zámerne bez hookov: panel, ktorý ho používa, má skoré `return null`,
 * takže komponent musí byť použiteľný bez ohľadu na poradie vykreslenia.
 */
import React from 'react';
import { STANDARD_FIBER_COUNTS, MAX_FIBER_COUNT } from '../../utils/fiberColors';

interface FiberCountPickerProps {
  value: number;
  onChange: (count: number) => void;
  id?: string;
  disabled?: boolean;
  /** Text pod voľbou — napr. upozornenie pri úprave existujúceho kábla */
  hint?: React.ReactNode;
}

export const FiberCountPicker: React.FC<FiberCountPickerProps> = ({
  value,
  onChange,
  id = 'fiber-count',
  disabled = false,
  hint,
}) => {
  const jeStandardny = STANDARD_FIBER_COUNTS.includes(value);

  const zmenNaPlatny = (raw: string) => {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    onChange(Math.max(1, Math.min(n, MAX_FIBER_COUNT)));
  };

  return (
    <div className="fiber-count-picker">
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <select
          id={id}
          value={value}
          disabled={disabled}
          onChange={(e) => zmenNaPlatny(e.target.value)}
          style={{ flex: 1, minWidth: 0 }}
        >
          {/* Vlastná hodnota mimo zoznamu — nech select neostane prázdny */}
          {!jeStandardny && (
            <option value={value}>{value} vlákien (vlastné)</option>
          )}
          {STANDARD_FIBER_COUNTS.map((n) => (
            <option key={n} value={n}>
              {n} {n === 1 ? 'vlákno' : n < 5 ? 'vlákna' : 'vlákien'}
            </option>
          ))}
        </select>
        <input
          type="number"
          aria-label="Vlastný počet vlákien"
          title={`Vlastný počet vlákien (1 až ${MAX_FIBER_COUNT})`}
          value={value}
          disabled={disabled}
          min={1}
          max={MAX_FIBER_COUNT}
          step={1}
          onChange={(e) => zmenNaPlatny(e.target.value)}
          style={{ width: '80px', flexShrink: 0 }}
        />
      </div>
      {hint && <small className="form-hint">{hint}</small>}
    </div>
  );
};

export default FiberCountPicker;
